/**
 * The volunteering desk.
 *
 * The flow, in order down the page:
 *   sign-ups awaiting confirmation → [Confirm]
 *   confirmed team                 → [Mark done] → writes verified hours
 *   open departments across all upcoming events
 *   manually entered hours         → [Verify]
 *   leaderboard for the year
 *
 * Hours are only ever credited on completion. Signing up is not the same as
 * turning up. SPEC.md §6.3.
 */

import { bibNo, formatDate, today, volunteerHours } from '../../data';
import { useClubData } from '../../context/ClubContext';
import { useUI } from '../../context/UIContext';
import { DeptCard } from '../../components/DeptCard';
import { ConfirmSheet, DeptTeamSheet } from '../../components/sheets/ClubSheets';
import { Button, Empty, Eyebrow, Stat, Stats, Table, ViewTitle } from '../../components/ui';

export function VolunteeringPage() {
  const { db, confirmSignup, completeSignup, verifyHours, removeDepartment } = useClubData();
  const { openSheet, toast } = useUI();

  const year = String(new Date().getFullYear());
  const now = today();

  /** Departments on events that have not happened yet. */
  const openDepts = db.departments.filter((d) => {
    const event = db.events.find((e) => e.id === d.eventId);
    return !!event && event.date >= now;
  });
  const totalNeeded = openDepts.reduce((sum, d) => sum + d.needed, 0);
  const totalFilled = openDepts.reduce(
    (sum, d) => sum + db.signups.filter((s) => s.deptId === d.id).length,
    0,
  );

  const rows = db.signups
    .map((signup) => ({
      signup,
      dept: db.departments.find((d) => d.id === signup.deptId),
      member: db.members.find((m) => m.id === signup.memberId),
    }))
    // Rows below assume dept and member are both present.
    .filter((r) => !!r.dept && !!r.member);

  const awaiting = rows.filter((r) => r.signup.status === 'signed');
  const confirmed = rows.filter((r) => r.signup.status === 'confirmed');
  const toVerify = db.volunteer.filter((v) => !v.verified);

  const leaderboard = db.members
    .map((m) => ({ member: m, hours: volunteerHours(db.volunteer, m.id, year) }))
    .filter((x) => x.hours > 0)
    .sort((a, b) => b.hours - a.hours);

  const signupHead = (
    <tr>
      <th>Member</th>
      <th>Bib</th>
      <th>Department</th>
      <th>Hrs</th>
      <th />
    </tr>
  );

  return (
    <>
      <ViewTitle
        title="Volunteering"
        sub="Departments are posted inside each event — members sign up from there"
      />

      <Stats>
        <Stat
          tone="good"
          numberTone="good"
          value={
            <>
              {totalFilled}
              <span className="text-copy-label">/{totalNeeded}</span>
            </>
          }
          label="Slots filled"
        />
        <Stat tone="amber" value={awaiting.length} label="To confirm" />
        <Stat value={confirmed.length} label="Confirmed" />
        <Stat
          tone={toVerify.length ? 'bad' : 'plain'}
          numberTone={toVerify.length ? 'bad' : undefined}
          value={toVerify.length}
          label="Hours to verify"
        />
      </Stats>

      <Eyebrow>Sign-ups awaiting confirmation ({awaiting.length})</Eyebrow>
      {awaiting.length ? (
        <Table head={signupHead}>
          {awaiting.map(({ signup, dept, member }) => (
            <tr key={signup.id}>
              <td className="font-market font-bold text-ink-700">{member.name}</td>
              <td>{bibNo(member.bib)}</td>
              <td>{dept.name}</td>
              <td>{dept.hours}h</td>
              <td>
                <Button
                  tone="v"
                  small
                  onClick={async () => {
                    await confirmSignup(signup.id);
                    toast('Confirmed');
                  }}
                >
                  Confirm
                </Button>
              </td>
            </tr>
          ))}
        </Table>
      ) : (
        <Empty padding={24}>Nothing waiting.</Empty>
      )}

      <Eyebrow>Confirmed team ({confirmed.length})</Eyebrow>
      {confirmed.length ? (
        <Table head={signupHead}>
          {confirmed.map(({ signup, dept, member }) => (
            <tr key={signup.id}>
              <td className="font-market font-bold text-ink-700">{member.name}</td>
              <td>{bibNo(member.bib)}</td>
              <td>{dept.name}</td>
              <td>{dept.hours}h</td>
              <td>
                <Button
                  tone="g"
                  small
                  onClick={async () => {
                    await completeSignup(signup.id);
                    toast('Duty completed — hours credited');
                  }}
                >
                  Mark done
                </Button>
              </td>
            </tr>
          ))}
        </Table>
      ) : (
        <Empty padding={24}>No one confirmed yet.</Empty>
      )}

      <Eyebrow>Open departments</Eyebrow>
      {openDepts.length ? (
        openDepts.map((d) => (
          <DeptCard
            key={d.id}
            dept={d}
            db={db}
            meId={null}
            mode="desk"
            onViewTeam={(id) => {
              const dept = db.departments.find((x) => x.id === id);
              if (dept) openSheet(<DeptTeamSheet dept={dept} db={db} />);
            }}
            onRemove={(id) => {
              const dept = db.departments.find((x) => x.id === id);
              const signed = db.signups.filter((sx) => sx.deptId === id).length;
              openSheet(
                <ConfirmSheet
                  title={`Remove ${dept?.name ?? 'this work'}?`}
                  body={
                    signed
                      ? `${signed} member${signed === 1 ? ' has' : 's have'} signed up for this. Removing it takes their place${signed === 1 ? '' : 's'} with it.`
                      : 'Nobody has signed up yet, so nothing else is affected.'
                  }
                  confirmLabel="Remove work"
                  doneMessage="Work removed"
                  onConfirm={() => removeDepartment(id)}
                />,
              );
            }}
          />
        ))
      ) : (
        <Empty padding={24}>No work posted. Open an event to post one.</Empty>
      )}

      <Eyebrow>Hours to verify</Eyebrow>
      {toVerify.length ? (
        <Table
          head={
            <tr>
              <th>Member</th>
              <th>Duty</th>
              <th>Date</th>
              <th>Hrs</th>
              <th />
            </tr>
          }
        >
          {toVerify.map((v) => {
            const member = db.members.find((m) => m.id === v.memberId);
            return (
              <tr key={v.id}>
                <td className="font-market font-bold text-ink-700">{member?.name ?? '—'}</td>
                <td>{v.role}</td>
                <td>{formatDate(v.date)}</td>
                <td>{v.hours}</td>
                <td>
                  <Button
                    tone="v"
                    small
                    onClick={async () => {
                      await verifyHours(v.id);
                      toast('Hours verified');
                    }}
                  >
                    Verify
                  </Button>
                </td>
              </tr>
            );
          })}
        </Table>
      ) : (
        <Empty padding={24}>All verified.</Empty>
      )}

      <Eyebrow>Leaderboard · {year}</Eyebrow>
      {leaderboard.length ? (
        <Table
          head={
            <tr>
              <th>#</th>
              <th>Member</th>
              <th>Hours</th>
            </tr>
          }
        >
          {leaderboard.map((x, i) => (
            <tr key={x.member.id}>
              <td>{i + 1}</td>
              <td className="font-market font-bold text-ink-700">{x.member.name}</td>
              <td>
                <b>{x.hours}</b>
              </td>
            </tr>
          ))}
        </Table>
      ) : (
        <Empty>No hours yet.</Empty>
      )}
    </>
  );
}
